import React, { useState } from "react";
import "../../Styles/AnnadataSukhibhavaForm.css";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { annadataSukhibhavaAPI } from "../../api/index";

// Where to go after a successful submit.
// For the Admin dashboard change this to "/AdminDashboard/AnnadataSukhibhavaTable".
const TABLE_ROUTE = "/dashboard/AnnadataSukhibhavaTable";

const MANDAL_NAMES = ["Kandukur", "Lingasamudram", "Gudluru", "Ulavapadu", "Voletivaripalem"];

const STATUS_OPTIONS = ["pending", "approved", "rejected", "disbursed"];
const STATUS_LABEL = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
  disbursed: "Disbursed",
};

const INITIAL_FORM = {
  farmerName: "",
  relation: "",
  landExtent: "",
  surveyNumber: "",
  aadhaar: "",
  voterId: "",
  mobile: "",
  bankAccount: "",
  ifsc: "",
  amount: "20000",
  mandal: "",
  village: "",
  date: "",
  status: "",
  photoUrl: "",
  documentUrl: "",
  remarks: "",
};

// Same order as the form layout (used to focus the first invalid field on submit)
const FIELD_ORDER = [
  "farmerName",
  "relation",
  "landExtent",
  "surveyNumber",
  "aadhaar",
  "voterId",
  "mobile",
  "bankAccount",
  "ifsc",
  "mandal",
  "village",
  "date",
  "status",
  "photoUrl",
  "documentUrl",
  "remarks",
];

// Fields that must be filled (these all have a * on the form).
// Photo URL, Supporting Document URL and Remarks are optional, but if they are
// filled they must still be valid.
// To make a field optional, remove it from this list and delete the * on its label.
const REQUIRED_FIELDS = [
  "farmerName",
  "relation",
  "landExtent",
  "surveyNumber",
  "aadhaar",
  "voterId",
  "mobile",
  "bankAccount",
  "ifsc",
  "mandal",
  "village",
  "date",
  "status",
];

// These fields show a format error immediately while typing
const LIVE_VALIDATE_FIELDS = [
  "landExtent",
  "aadhaar",
  "voterId",
  "mobile",
  "bankAccount",
  "ifsc",
  "village",
];

// Digits only (other characters are blocked automatically)
const DIGITS_ONLY_FIELDS = ["aadhaar", "mobile", "bankAccount"];

// Converted to capitals automatically (spaces removed)
const UPPERCASE_FIELDS = ["voterId", "ifsc", "surveyNumber"];

const NAME_REGEX = /^[A-Za-z\s.']+$/;
const RELATION_REGEX = /^[A-Za-z\s./'\-]+$/;
const SURVEY_REGEX = /^[A-Z0-9\-\/,]+$/;
const VILLAGE_REGEX = /^[A-Za-z\s]+$/;
const VOTER_ID_REGEX = /^[A-Z]{3}[0-9]{7}$/;
const AADHAAR_REGEX = /^[2-9][0-9]{11}$/;
const MOBILE_REGEX = /^[6-9][0-9]{9}$/;
const BANK_ACCOUNT_REGEX = /^[0-9]{9,18}$/;
const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;

const extractErrorMessage = (err) => {
  if (!err.response) {
    return `Network error — could not reach the server (${err.message}).`;
  }
  const status = err.response.status;
  const detail = err.response.data?.detail ?? err.response.data?.message;

  if (status === 404) {
    return typeof detail === "string"
      ? `[404] ${detail} — double check the spelling of the Village/Mandal name.`
      : `[404] Village or Mandal not recognized. Check spelling and try again.`;
  }
  if (typeof detail === "string") return `[${status}] ${detail}`;
  if (Array.isArray(detail)) {
    const parts = detail.slice(0, 3).map((d) => {
      if (d.row !== undefined) return `Row ${d.row}: ${d.reason}`;
      if (d.loc) return `${d.loc.join(".")}: ${d.msg}`;
      return d.msg || d.reason || JSON.stringify(d);
    });
    return `[${status}] ${parts.join(" | ")}`;
  }
  return `[${status}] Request failed — see console for full response body`;
};

const isLikelyUrl = (url) => {
  if (!url.trim()) return true;
  try {
    const u = new URL(url);
    return u.protocol === "https:" || u.protocol === "http:";
  } catch {
    return false;
  }
};

// "1111111111" / "999999999999" style numbers are not real
const isRepeatedDigit = (s) => /^(\d)\1+$/.test(s);

// ---------- Field validation rules ----------
const validateField = (name, value) => {
  const v = typeof value === "string" ? value.trim() : value;
  const required = REQUIRED_FIELDS.includes(name);

  switch (name) {
    case "farmerName":
      if (!v) return "Farmer name is required";
      if (v.length < 3) return "Name must be at least 3 characters";
      if (v.length > 100) return "Name must not exceed 100 characters";
      if (!NAME_REGEX.test(v)) return "Name can contain only alphabets, spaces, '.' and '''";
      return "";

    case "relation":
      if (!v) return required ? "Relation is required (e.g. S/O Ramesh)" : "";
      if (v.length < 2) return "Relation must be at least 2 characters";
      if (v.length > 100) return "Relation must not exceed 100 characters";
      if (!RELATION_REGEX.test(v)) return "Relation can contain only alphabets, spaces and . / ' -";
      if ((v.match(/[A-Za-z]/g) || []).length < 2)
        return "Relation must contain at least 2 letters (e.g. S/O Ramesh)";
      return "";

    case "landExtent": {
      if (!v) return required ? "Land extent is required" : "";
      if (!/^\d+(\.\d{0,2})?$/.test(v))
        return "Enter a valid land extent (up to 2 decimal places, e.g. 2.50)";
      const n = Number(v);
      if (n <= 0) return "Land extent must be greater than 0";
      if (n > 1000) return "Land extent cannot be more than 1000 acres";
      return "";
    }

    case "surveyNumber":
      if (!v) return required ? "Survey number is required" : "";
      if (v.length > 30) return "Survey number must not exceed 30 characters";
      if (!SURVEY_REGEX.test(v))
        return "Survey number can contain only letters, digits, '-', '/' and ','";
      if (!/[0-9]/.test(v)) return "Survey number must contain at least one digit (e.g. 123/4A)";
      return "";

    case "aadhaar":
      if (!v) return required ? "Aadhaar number is required" : "";
      if (!/^\d+$/.test(v)) return "Aadhaar number must contain digits only";
      if (v.length < 12) return `Aadhaar number must be exactly 12 digits (${v.length}/12 entered)`;
      if (v.length > 12) return "Aadhaar number must be exactly 12 digits";
      if (!AADHAAR_REGEX.test(v)) return "Aadhaar number cannot start with 0 or 1";
      if (isRepeatedDigit(v)) return "Enter a valid Aadhaar number";
      return "";

    case "voterId":
      if (!v) return required ? "Voter Id is required" : "";
      if (v.length < 10) return `Voter Id must be 10 characters (${v.length}/10 entered)`;
      if (!VOTER_ID_REGEX.test(v))
        return "Enter a valid Voter Id (3 letters + 7 digits, e.g. ABC1234567)";
      return "";

    case "mobile":
      if (!v) return required ? "Mobile number is required" : "";
      if (!/^\d+$/.test(v)) return "Mobile number must contain digits only";
      if (!/^[6-9]/.test(v)) return "Mobile number must start with 6, 7, 8 or 9";
      if (v.length < 10) return `Mobile number must be exactly 10 digits (${v.length}/10 entered)`;
      if (v.length > 10) return "Mobile number must be exactly 10 digits";
      if (!MOBILE_REGEX.test(v)) return "Enter a valid mobile number";
      if (isRepeatedDigit(v)) return "Enter a valid mobile number";
      return "";

    case "bankAccount":
      if (!v) return required ? "Bank account number is required" : "";
      if (!/^\d+$/.test(v)) return "Bank account number must contain digits only";
      if (v.length < 9) return `Bank account number must be 9 to 18 digits (${v.length} entered)`;
      if (!BANK_ACCOUNT_REGEX.test(v)) return "Bank account number must be 9 to 18 digits";
      if (isRepeatedDigit(v)) return "Enter a valid bank account number";
      return "";

    case "ifsc":
      if (!v) return required ? "IFSC code is required" : "";
      if (v.length < 11) return `IFSC code must be 11 characters (${v.length}/11 entered)`;
      if (!IFSC_REGEX.test(v))
        return "Enter a valid IFSC code (4 letters + 0 + 6 letters/digits, e.g. SBIN0001234)";
      return "";

    case "mandal":
      if (!v) return "Please select a Mandal";
      if (!MANDAL_NAMES.includes(v)) return "Please select a valid Mandal";
      return "";

    case "village":
      if (!v) return "Village is required";
      if (!VILLAGE_REGEX.test(v)) return "Village name can contain only alphabets (A-Z)";
      if (v.length < 2) return "Village name must be at least 2 characters";
      if (v.length > 100) return "Village name must not exceed 100 characters";
      return "";

    case "date": {
      if (!v) return required ? "Date is required" : "";
      if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return "Enter a valid date";
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return "Enter a valid date";
      if (d.getFullYear() < 1990) return "Date cannot be before 1990";
      if (d.getFullYear() > 2100) return "Date cannot be after 2100";
      return "";
    }

    case "status":
      if (!v) return "Please select a Status";
      if (!STATUS_OPTIONS.includes(v)) return "Please select a valid Status";
      return "";

    case "photoUrl":
    case "documentUrl":
      if (!v) return ""; // optional
      if (!isLikelyUrl(v)) return "Enter a valid link starting with http:// or https://";
      return "";

    case "remarks":
      if (!v) return ""; // optional
      if (v.length < 5) return "Remarks must be at least 5 characters";
      if (v.length > 500) return "Remarks must not exceed 500 characters";
      return "";

    default:
      return "";
  }
};

const validateAll = (data) => {
  const errors = {};
  FIELD_ORDER.forEach((name) => {
    const msg = validateField(name, data[name]);
    if (msg) errors[name] = msg;
  });
  return errors;
};

// Land extent: digits and one decimal point, max 2 decimal places
const formatLandExtent = (raw) => {
  let value = raw.replace(/[^0-9.]/g, "");
  const parts = value.split(".");
  if (parts.length > 2) value = `${parts[0]}.${parts.slice(1).join("")}`;
  const [whole, decimals] = value.split(".");
  return decimals !== undefined ? `${whole}.${decimals.slice(0, 2)}` : value;
};

const AnnadataSukhibhavaForm = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const showError = (name) => touched[name] && errors[name];
  const inputClass = (name) => (showError(name) ? "input-error" : "");

  const handleChange = (e) => {
    const { name } = e.target;
    let { value } = e.target;

    // Auto-format specific fields
    if (UPPERCASE_FIELDS.includes(name)) {
      value = value.toUpperCase().replace(/\s/g, "");
    }
    if (DIGITS_ONLY_FIELDS.includes(name)) {
      value = value.replace(/\D/g, "");
    }
    if (name === "landExtent") {
      value = formatLandExtent(value);
    }

    // Changing the Mandal clears the Village (it must belong to the chosen Mandal)
    if (name === "mandal") {
      setFormData((prev) => ({ ...prev, mandal: value, village: "" }));
      setErrors((prev) => ({ ...prev, mandal: validateField("mandal", value), village: "" }));
      setTouched((prev) => ({ ...prev, mandal: true, village: false }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));

    const isLive = LIVE_VALIDATE_FIELDS.includes(name);
    if (touched[name] || isLive) {
      if (isLive && !touched[name]) {
        setTouched((prev) => ({ ...prev, [name]: true }));
      }
      setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  const handleReset = () => {
    setFormData(INITIAL_FORM);
    setErrors({});
    setTouched({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate every field and mark all as touched so every error is shown
    const newErrors = validateAll(formData);
    setErrors(newErrors);
    setTouched(FIELD_ORDER.reduce((acc, k) => ({ ...acc, [k]: true }), {}));

    const firstInvalid = FIELD_ORDER.find((f) => newErrors[f]);
    if (firstInvalid) {
      toast.error("Please fix the highlighted fields");
      const el = document.querySelector(`[name="${firstInvalid}"]`);
      if (el) el.focus();
      return;
    }

    const payload = {
      farmer_name: formData.farmerName.trim(),
      relation_name: formData.relation.trim(),
      land_extent_acres: Number(formData.landExtent),
      survey_number: formData.surveyNumber.trim(),
      aadhaar_number: formData.aadhaar.trim(),
      epic_no: formData.voterId.trim(),
      mobile_number: formData.mobile.trim(),
      bank_account_number: formData.bankAccount.trim(),
      ifsc_code: formData.ifsc.trim(),
      amount: Number(formData.amount),
      mandal_name: formData.mandal,
      village_name: formData.village.trim(),
      application_date: formData.date,
      status: formData.status,
      photo_url: formData.photoUrl.trim() || undefined,
      document_url: formData.documentUrl.trim() || undefined,
      remarks: formData.remarks.trim() || undefined,
    };

    setSubmitting(true);
    try {
      console.log("Annadata Sukhibhava create payload sent:", payload);
      await annadataSukhibhavaAPI.create(payload);

      // Success toast — stays visible after navigating to the table page
      toast.success("Form submitted successfully", { toastId: "annadata-form-success" });

      handleReset();
      navigate(TABLE_ROUTE);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="annadata-form-container">
      <div className="annadata-form-card">
        <div className="annadata-form-header">
          <h2>Add New Annadata Sukhibhava Beneficiary</h2>
          <p>₹20,000 annual financial assistance for eligible farmers</p>
          <button type="button" className="annadata-back-btn" onClick={() => navigate(-1)}>
            Back
          </button>
        </div>

        {/* noValidate -> disables browser default popups so our custom messages show */}
        <form onSubmit={handleSubmit} noValidate>
          <div className="annadata-form-grid">
            <div className="annadata-form-group">
              <label>Farmer Name *</label>
              <input
                type="text"
                name="farmerName"
                placeholder="Enter Farmer Name"
                value={formData.farmerName}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("farmerName")}
                maxLength={100}
              />
              {showError("farmerName") && <span className="error-text">{errors.farmerName}</span>}
            </div>

            <div className="annadata-form-group">
              <label>Relation (Ex: S/O, W/O) *</label>
              <input
                type="text"
                name="relation"
                placeholder="Ex: S/O Ramesh, W/O Suresh"
                value={formData.relation}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("relation")}
                maxLength={100}
              />
              {showError("relation") && <span className="error-text">{errors.relation}</span>}
            </div>

            <div className="annadata-form-group">
              <label>Land Extent (Acres) *</label>
              <input
                type="text"
                inputMode="decimal"
                name="landExtent"
                placeholder="Enter Land Extent (e.g. 2.50)"
                value={formData.landExtent}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("landExtent")}
                maxLength={7}
              />
              {showError("landExtent") && <span className="error-text">{errors.landExtent}</span>}
            </div>

            <div className="annadata-form-group">
              <label>Survey Number *</label>
              <input
                type="text"
                name="surveyNumber"
                placeholder="Enter Survey Number (e.g. 123/4A)"
                value={formData.surveyNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("surveyNumber")}
                maxLength={30}
              />
              {showError("surveyNumber") && (
                <span className="error-text">{errors.surveyNumber}</span>
              )}
            </div>

            <div className="annadata-form-group">
              <label>Aadhaar Number *</label>
              <input
                type="text"
                inputMode="numeric"
                name="aadhaar"
                placeholder="Enter 12-digit Aadhaar Number"
                value={formData.aadhaar}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("aadhaar")}
                maxLength={12}
              />
              {showError("aadhaar") && <span className="error-text">{errors.aadhaar}</span>}
            </div>

            <div className="annadata-form-group">
              <label>Voter Id *</label>
              <input
                type="text"
                name="voterId"
                placeholder="Enter Voter Id (e.g. ABC1234567)"
                value={formData.voterId}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("voterId")}
                maxLength={10}
              />
              {showError("voterId") && <span className="error-text">{errors.voterId}</span>}
            </div>

            <div className="annadata-form-group">
              <label>Mobile Number *</label>
              <input
                type="tel"
                inputMode="numeric"
                name="mobile"
                placeholder="Enter 10-digit Mobile Number"
                value={formData.mobile}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("mobile")}
                maxLength={10}
              />
              {showError("mobile") && <span className="error-text">{errors.mobile}</span>}
            </div>

            <div className="annadata-form-group">
              <label>Bank Account Number *</label>
              <input
                type="text"
                inputMode="numeric"
                name="bankAccount"
                placeholder="Enter Bank Account Number"
                value={formData.bankAccount}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("bankAccount")}
                maxLength={18}
              />
              {showError("bankAccount") && (
                <span className="error-text">{errors.bankAccount}</span>
              )}
            </div>

            <div className="annadata-form-group">
              <label>IFSC Code *</label>
              <input
                type="text"
                name="ifsc"
                placeholder="Enter IFSC Code (e.g. SBIN0001234)"
                value={formData.ifsc}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("ifsc")}
                maxLength={11}
              />
              {showError("ifsc") && <span className="error-text">{errors.ifsc}</span>}
            </div>

            <div className="annadata-form-group">
              <label>Annual Amount (₹) *</label>
              <input type="number" name="amount" value={formData.amount} readOnly />
            </div>

            {/* Mandal first ... */}
            <div className="annadata-form-group">
              <label>Mandal *</label>
              <select
                name="mandal"
                value={formData.mandal}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("mandal")}
              >
                <option value="">Select Mandal</option>
                {MANDAL_NAMES.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
              {showError("mandal") && <span className="error-text">{errors.mandal}</span>}
            </div>

            {/* ... then Village */}
            <div className="annadata-form-group">
              <label>Village *</label>
              <input
                type="text"
                name="village"
                placeholder={formData.mandal ? "Enter Village (alphabets only)" : "Select Mandal first"}
                value={formData.village}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("village")}
                maxLength={100}
                disabled={!formData.mandal}
              />
              {showError("village") && <span className="error-text">{errors.village}</span>}
              <small style={{ color: "#9ca3af", fontSize: "12px", marginTop: "4px", display: "block" }}>
                Must exactly match an existing village name under the selected Mandal.
              </small>
            </div>

            <div className="annadata-form-group">
              <label>Date *</label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("date")}
                min="1990-01-01"
                max="2100-12-31"
              />
              {showError("date") && <span className="error-text">{errors.date}</span>}
            </div>

            <div className="annadata-form-group">
              <label>Status *</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("status")}
              >
                <option value="">Select Status</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                ))}
              </select>
              {showError("status") && <span className="error-text">{errors.status}</span>}
            </div>

            <div className="annadata-form-group">
              <label>Photo URL (Optional)</label>
              <input
                type="url"
                name="photoUrl"
                placeholder="https://..."
                value={formData.photoUrl}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("photoUrl")}
              />
              {showError("photoUrl") && <span className="error-text">{errors.photoUrl}</span>}
            </div>

            <div className="annadata-form-group">
              <label>Supporting Document URL (Optional)</label>
              <input
                type="url"
                name="documentUrl"
                placeholder="https://..."
                value={formData.documentUrl}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("documentUrl")}
              />
              {showError("documentUrl") && (
                <span className="error-text">{errors.documentUrl}</span>
              )}
            </div>
          </div>

          <div className="annadata-form-group annadata-full-width">
            <label>Remarks (Optional)</label>
            <textarea
              rows="4"
              name="remarks"
              placeholder="Enter Remarks"
              value={formData.remarks}
              onChange={handleChange}
              onBlur={handleBlur}
              className={inputClass("remarks")}
              maxLength={500}
            ></textarea>
            {showError("remarks") && <span className="error-text">{errors.remarks}</span>}
            <small className="char-count">{formData.remarks.length}/500</small>
          </div>

          <div className="annadata-btn-group">
            <button type="submit" className="annadata-submit-btn" disabled={submitting}>
              {submitting ? "Submitting..." : "Submit"}
            </button>
            <button
              type="button"
              className="annadata-reset-btn"
              onClick={handleReset}
              disabled={submitting}
            >
              Reset
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AnnadataSukhibhavaForm;